<template>
  <div :id="containerId" class="trading-view-chart"></div>
</template>
<script>
import rf from "@/request/RequestFactory";
import TimezoneUtils from "@/common/TimezoneUtils";
import LocalStorageUtils from "@/common/LocalStorageUtils";
import Const from "@/common/Const";
import { mapState } from "vuex";
import Cookies from "js-cookie";

export default {
  components: {},
  props: {
    multipleChart: { type: Boolean, default: false },
    symbol: { default: "btc/usdt", type: String },
    resolution: { default: "15", type: String },
    minTick: { default: "default", type: String },
  },
  data() {
    var dataFeed = new Datafeeds.UDFCompatibleDatafeed(
      process.env.VUE_APP_API_URL + "/api/v1/chart"
    );
    dataFeed.getBars = this.getBars.bind(this);
    dataFeed.subscribeBars = this.subscribeBars.bind(this);
    return {
      tradingViewChart: null,
      realtimeDatafeed: dataFeed,
      coin: Const.DEFAULT_COIN,
      currency: Const.DEFAULT_CURRENCY,
      lastBar: undefined,
      onRealtimeCallback: undefined,
      disableByBetaTester: false,
      theme:
        Cookies.get("user-theme", { domain: process.env.VUE_APP_PUBLIC_URL }) ||
        "dark-theme",
      greenColor: "#54DD7A",
      redColor: "#FF6666",
      blueColor: "#3A7DFF",
      lightColor: "#FBFBFB",
      darkColor: "#1B1D24",
      textColor: "#9EA5B3",
      darkGridColor: "#2D303B",
      lightGridColor: "#D9DCE1",
      currentPrices: null,
    };
  },
  computed: {
    containerId() {
      const id = "trading-view-chart";
      if (this.multipleChart) {
        return `${id}-${Date.now()}`;
      }
      return id;
    },
    ...mapState({
      pairCoins: (state) => state.pairCoins,
      userThemeMode: (state) => state.userThemeMode,
    }),
  },
  methods: {
    subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID) {
      this.onRealtimeCallback = onRealtimeCallback;
    },
    fillDataBars(bars, disableByBetaTester) {
      bars = window._.forEach(bars, function (bar) {
        bar.time = parseInt(bar.time);
        bar.open = disableByBetaTester == true ? 0 : parseFloat(bar.open);
        bar.close = disableByBetaTester == true ? 0 : parseFloat(bar.close);
        bar.high = disableByBetaTester == true ? 0 : parseFloat(bar.high);
        bar.low = disableByBetaTester == true ? 0 : parseFloat(bar.low);
        bar.volume = disableByBetaTester == true ? 0 : parseFloat(bar.volume);
      });
      return bars;
    },
    getBars(
      symbolInfo,
      resolution,
      from,
      to,
      onHistoryCallback,
      _onErrorCallback,
      _firstDataRequest,
    ) {
      this.$emit("update:resolution", this.resolution);
      let ticker = symbolInfo.ticker.split("/");
      this.coin = ticker[0];
      this.currency = ticker[1];
      let params = {
        coin: this.coin,
        currency: this.currency,
        resolution: this.resolutionToApiFormat(resolution),
        from: from * 1000,
        to: to * 1000,
      };
      let self = this;
      rf.getRequest("PriceRequest")
        .getChartBars(params)
        .then((bars) => {
          bars = window._.forEach(bars, function (bar) {
            bar.time = parseInt(bar.time);
            bar.open = parseFloat(bar.open);
            bar.close = parseFloat(bar.close);
            bar.high = parseFloat(bar.high);
            bar.low = parseFloat(bar.low);
            bar.volume = parseFloat(bar.volume);
          });
          if (bars.length) {
            let currentTime = Math.floor(Date.now() / resolution) * resolution;
            let lastBar = bars[bars.length - 1];
            if (lastBar.time > currentTime) {
              bars.pop();
            }
          }
          if (bars.length) {
            self.lastBar = bars[bars.length - 1];
          }
          onHistoryCallback(bars, { noData: bars.length == 0 });
        });
    },

    onPricesUpdated(prices) {
      this.currentPrices = prices;
    },

    getSocketEventHandlers() {
      return {
        OrderTransactionCreated: this.onOrderTransactionCreated,
        PricesUpdated: this.onPricesUpdated,
      };
    },

    onOrderTransactionCreated(data) {
      if (
        this.currency.toLowerCase() != data?.currency ||
        this.coin.toLowerCase() != data?.coin
      ) {
        return;
      }

      let price = parseFloat(data.price);
      let createdAt = new Date(data.createdAt).getTime();
      let volume = parseFloat(data.quantity);
      let resolution = this.resolutionToMilliseconds(this.resolution);
      let periodStart = Math.floor(createdAt / resolution) * resolution;
      let existingBar = window._.cloneDeep(this.lastBar);

      if (this.lastBar) {
        if (periodStart === this.lastBar.time) {
          existingBar.volume += volume;
          existingBar.high = Math.max(price, existingBar.high);
          existingBar.low = Math.min(price, existingBar.low);
          existingBar.close = price;
        } else {
          existingBar = {
            time: periodStart,
            open: existingBar.close,
            high: Math.max(price, existingBar.close),
            low: Math.min(price, existingBar.close),
            close: price,
            volume: volume,
          };
        }
      } else {
        existingBar = {
          time: periodStart,
          open: price,
          high: price,
          low: price,
          close: price,
          volume: volume,
        };
      }

      this.lastBar = existingBar;
      if (this.onRealtimeCallback) {
        this.onRealtimeCallback(existingBar);
      }
    },

    createEmptyBarIfNeeded() {
      let createdAt = Date.now();
      let resolution = this.resolutionToMilliseconds(this.resolution);
      let periodStart = Math.floor(createdAt / resolution) * resolution;

      if (this.lastBar && periodStart > this.lastBar.time) {
        let price = this.lastBar.close;
        this.lastBar = {
          time: periodStart,
          open: price,
          high: price,
          low: price,
          close: price,
          volume: 0,
        };
        if (this.onRealtimeCallback) {
          this.onRealtimeCallback(this.lastBar);
        }
      }
    },
    resolutionToApiFormat(resolution) {
      const resolutionMap = {
        D: "1D",
        "1D": "1D",
        W: "1W",
        "1W": "1W",
        M: "1M",
        "1M": "1M",
      };

      if (resolutionMap[resolution]) {
        return resolutionMap[resolution];
      }

      return resolution.toString();
    },

    resolutionToSeconds(resolution) {
      var minutes = parseInt(resolution);
      if (isNaN(minutes)) {
        minutes = Const.CHARTING_RESOLUTION_MULTIPLICITY[resolution];
      }
      return minutes * 60;
    },

    resolutionToMilliseconds(resolution) {
      var minutes = parseInt(resolution);
      if (isNaN(minutes)) {
        minutes = Const.CHARTING_RESOLUTION_MULTIPLICITY[resolution];
      }
      return minutes * 60 * 1000;
    },
    resolutionToApiFormat(resolution) {
      const resolutionMap = {
        D: "1D",
        "1D": "1D",
        W: "1W",
        "1W": "1W",
        M: "1M",
        "1M": "1M",
      };

      if (resolutionMap[resolution]) {
        return resolutionMap[resolution];
      }

      return resolution.toString();
    },
    createChart() {
      let disabled_features = [
        "use_localstorage_for_settings",
        // "left_toolbar",
        "context_menus",
        "display_market_status",
        "timeframes_toolbar",
        "header_compare",
        "header_undo_redo",
        "header_saveload",
        "header_screenshot",
        "header_interval_dialog_button",
        "volume_force_overlay",
        "control_bar",
        "border_around_the_chart",
        "header_widget",
        "header_symbol_search",
        "chart_property_page_scales",
        "link_to_tradingview",
      ];
      this.tradingViewChart = new TradingView.widget({
        symbol: this.symbol,
        interval: this.resolution,
        locale: window.i18n.locale,
        timezone: TimezoneUtils.getTimezone(),
        theme: "light",
        container_id: this.containerId,
        datafeed: this.realtimeDatafeed,
        library_path:
          window.location.protocol +
          "//" +
          window.location.host +
          "/tradingview/charting_library/",
        autosize: true,
        withdateranges: false,
        client_id: "vcc",
        user_id: "public_user_id",
        save: true,
        drawings_access: {
          type: "black",
          tools: [{ name: "Regression Trend" }],
        },
        disabled_features: disabled_features,
        indicators_file_name: "/js/indexes.js",
        custom_css_url:
          this.theme === "light-theme"
            ? "tradingview.css"
            : "tradingview-dark.css",
        enabled_features: [
          "dont_show_boolean_study_arguments",
          "hide_last_na_study_output",
        ],
        logo: {
          image: "@/assets/images/villbit-dark.svg",
          link: "@/assets/images/villbit-dark.svg",
        },
        loading_screen: {
          backgroundColor: "transparent",
        },
        toolbar_bg: "transparent",

        overrides: {
          "paneProperties.topMargin": 15,
          "scalesProperties.showStudyLastValue": true,
          "paneProperties.background":
            this.theme === "light-theme" ? this.lightColor : this.darkColor,
          "mainSeriesProperties.ma.upColor": this.greenColor,
          "mainSeriesProperties.ba.upColor": this.greenColor,
          "mainSeriesProperties.candleStyle.upColor": this.greenColor,
          "mainSeriesProperties.candleStyle.downColor": this.redColor,
          "mainSeriesProperties.candleStyle.drawWick": true,
          "mainSeriesProperties.candleStyle.drawBorder": true,
          "mainSeriesProperties.candleStyle.borderColor": this.greenColor,
          "mainSeriesProperties.candleStyle.borderUpColor": this.greenColor,
          "mainSeriesProperties.candleStyle.borderDownColor": this.redColor,
          "mainSeriesProperties.candleStyle.wickUpColor": this.greenColor,
          "mainSeriesProperties.candleStyle.wickDownColor": this.redColor,
          "mainSeriesProperties.candleStyle.barColorsOnPrevClose": false,
          "mainSeriesProperties.minTick": this.minTick,
          "paneProperties.vertGridProperties.color":
            this.theme === "light-theme"
              ? this.lightGridColor
              : this.darkGridColor,
          "paneProperties.horzGridProperties.color":
            this.theme === "light-theme"
              ? this.lightGridColor
              : this.darkGridColor,
          "scalesProperties.lineColor":
            this.theme === "light-theme"
              ? this.lightGridColor
              : this.darkGridColor,
          "scalesProperties.textColor": this.textColor,
        },
        studies_overrides: {
          "volume.volume.color.0": this.redColor,
          "volume.volume.color.1": this.greenColor,
          "volume.volume.transparency": 70,
          "volume.volume ma.color": this.redColor,
          "volume.volume ma.transparency": 0,
          "volume.volume ma.linewidth": 1,
          "volume.volume ma.plottype": "line",
          "volume.show ma": true,
        },
      });
      var self = this;
      this.tradingViewChart.onChartReady(function () {
        if (!self.reloadTrandingChart()) {
          self.tradingViewChart
            .chart()
            .createStudy("Moving Average", false, false, [20], null, {
              "Plot.linewidth": 1.3,
              "Plot.color": "#3A7DFF",
            });
          self.tradingViewChart
            .chart()
            .createStudy("Moving Average", false, false, [40], null, {
              "Plot.linewidth": 1.3,
              "Plot.color": "#54DD7A",
            });
        }
        self.$watch("minTick", self.watchMinTick);
      });
      window.Chart = this.tradingViewChart;
    },
    watchMinTick(newMinTick) {
      const overrides = Object.assign(
        {},
        this.tradingViewChart.options.overrides
      );
      overrides["mainSeriesProperties.minTick"] = newMinTick;
      this.tradingViewChart.applyOverrides(overrides);
    },
    showIndicatorDialog() {
      if (!window._.isEmpty(this.tradingViewChart)) {
        this.tradingViewChart.chart().executeActionById("insertIndicator");
      }
    },

    showChartPropertiesDialog() {
      if (!window._.isEmpty(this.tradingViewChart)) {
        this.tradingViewChart.chart().executeActionById("chartProperties");
      }
    },

    saveTradingChartConfiguration() {
      this.tradingViewChart.save((res) => {
        LocalStorageUtils.saveChart(
          Const.TRADING_VIEW_CHART.OBJECT.SPOT_EXCHANGE,
          JSON.stringify(res)
        );
      });
    },

    reloadTrandingChart() {
      const jsonObject = LocalStorageUtils.loadChart(
        Const.TRADING_VIEW_CHART.OBJECT.SPOT_EXCHANGE
      );
      if (jsonObject) {
        let url = new URL(window.location.href);
        let params = new URLSearchParams(url.search);
        let coin = params.get("coin");
        let currency = params.get("currency");
        jsonObject.charts[0].panes[0].sources[0].state.symbol = `${coin}/${currency}`;
        jsonObject.charts[0].panes[0].sources[0].state.shortName = `${coin}/${currency}`;
        this.tradingViewChart.load(jsonObject);
        return true;
      }
      return false;
    },

    setBarStyle(type) {
      this.tradingViewChart.chart().setChartType(type);
    },
    initData() {
      this.disableByBetaTester = false;
      let coin = this.$route.query.coin || Const.DEFAULT_COIN;
      let currency = this.$route.query.currency || Const.DEFAULT_CURRENCY;

      const setting = this.pairCoins;
      if (!setting.is_enable && setting.is_show_beta_tester) {
        this.disableByBetaTester = true;
        if (window.isAuthenticated) {
          rf.getRequest("UserRequest")
            .getUserPairTradingSetting({ coin, currency })
            .then((res) => {
              if (
                res &&
                res.data &&
                res.data.enable_trading == Const.ENABLE_TRADING.ENABLE
              ) {
                this.disableByBetaTester = false;
              }
              this.createChart();
            });
        }
      } else {
        this.createChart();
      }
      rf.getRequest("PriceRequest")
        .getPrices()
        .then((res) => {
          this.onPricesUpdated(res.data);
        });
    },
  },
  watch: {
    currentPrices: {
      handler(prices) {
        if (!prices) return;

        const pricesData = prices.data || prices;

        if (!pricesData || typeof pricesData !== "object") {
          console.warn("TradingViewChart - Invalid prices data:", prices);
          return;
        }

        let symbol = (this.coin + "/" + this.currency).toLowerCase();
        let tradingPair = (this.currency + "_" + this.coin).toLowerCase();


        let price = null;

        if (pricesData[symbol]) {
          price =
            typeof pricesData[symbol] === "object"
              ? parseFloat(pricesData[symbol].price)
              : parseFloat(pricesData[symbol]);
        }

        if (!price && pricesData[tradingPair]) {
          price =
            typeof pricesData[tradingPair] === "object"
              ? parseFloat(pricesData[tradingPair].price)
              : parseFloat(pricesData[tradingPair]);
        }

        if (!price) {
          symbol = (this.coin + "/" + this.currency).toUpperCase();
          if (pricesData[symbol]) {
            price =
              typeof pricesData[symbol] === "object"
                ? parseFloat(pricesData[symbol].price)
                : parseFloat(pricesData[symbol]);
          }
        }

        if (isNaN(price) || !price) {
          console.warn("TradingViewChart - Cannot find price for:", {
            coin: this.coin,
            currency: this.currency,
            tradingPair: tradingPair,
            availableKeys: Object.keys(pricesData),
            receivedData: prices,
          });
          return;
        }

        console.log("TradingViewChart - Price found:", {
          tradingPair: tradingPair,
          price: price,
        });

        let createdAt = Date.now();
        let resolution = this.resolutionToMilliseconds(this.resolution);
        let periodStart = Math.floor(createdAt / resolution) * resolution;
        let existingBar = this.lastBar;

        if (this.lastBar) {
          if (periodStart === this.lastBar.time) {
            existingBar.high =
              existingBar.high !== 0
                ? Math.max(price, existingBar.high)
                : price;
            existingBar.low =
              existingBar.low !== 0
                ? Math.min(price, existingBar.low)
                : price;
            existingBar.close = price;
          } else {
            existingBar = {
              time: periodStart,
              open: existingBar.close,
              high: Math.max(price, existingBar.close),
              low: Math.min(price, existingBar.close),
              close: price,
              volume: 0,
            };
          }
        } else {
          existingBar = {
            time: periodStart,
            open: price,
            high: price,
            low: price,
            close: price,
            volume: 0,
          };
        }

        this.lastBar = existingBar;
        if (this.onRealtimeCallback) {
          console.log("TradingViewChart - Updating candle:", {
            time: new Date(existingBar.time).toISOString(),
            OHLC: {
              open: existingBar.open,
              high: existingBar.high,
              low: existingBar.low,
              close: existingBar.close,
            },
            volume: existingBar.volume,
          });
          this.onRealtimeCallback(existingBar);
        } else {
          console.warn(
            "TradingViewChart - onRealtimeCallback is not set! TradingView not subscribed yet.",
          );
        }
      },
      deep: true,
    },
    userThemeMode(theme) {
      this.theme = theme;
      this.createChart();
    },
    symbol(newSymbol) {
      let ticker = newSymbol.split("/");
      this.coin = ticker[0].toLowerCase();
      this.currency = ticker[1].toLowerCase();
      this.lastBar = null;

      if (this.tradingViewChart) {
        this.tradingViewChart.setSymbol(
          newSymbol,
          this.resolution,
          function () {}
        );
      }
      this.initData();
    },
    resolution(newResolution) {
      this.tradingViewChart.setSymbol(
        this.symbol,
        newResolution,
        function () {}
      );
    },
    "$route.query.lang"() {
      this.initData();
    },
  },
  created() {
    if (this.symbol) {
      let ticker = this.symbol.split("/");
      this.coin = ticker[0].toLowerCase();
      this.currency = ticker[1].toLowerCase();
    }
  },
  mounted() {
    this.initData();
    var that = this;
    TradingView.onready(function () {
      that.createChart();
    });

    window.setInterval(() => {
      this.createEmptyBarIfNeeded();
    }, 15000);

    if (typeof this.tradingViewChart != "undefined") {
      this.createChart();
    }

    if (window.Socket) {
      this.onSocketConnect = () => {
        console.log("Connected:", window.Socket.id);
      };

      this.onSocketDisconnect = (reason) => {
        console.log("Disconnected:", reason);
        this.lastBar = undefined;
        this.onRealtimeCallback = undefined;
      };

      window.Socket.on("connect", this.onSocketConnect);
      window.Socket.on("disconnect", this.onSocketDisconnect);
    }
  },
  beforeDestroy() {
    if (window.Socket) {
      if (this.onSocketConnect) {
        window.Socket.off("connect", this.onSocketConnect);
      }
      if (this.onSocketDisconnect) {
        window.Socket.off("disconnect", this.onSocketDisconnect);
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.trading-view-chart {
  height: 100%;
}
</style>
